import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Add,
  ChakraBadge,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EmptyList,
  EquipUIFireflyV2Theme,
  MenuSelect,
  MenuSelectOption,
  PageCount,
  Pagination,
  Reset,
  RowObject,
  Table,
  TableColumn,
  UserAvatarCard,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';

import PatientTableCellToolTip from '~/components/common/PatientTableCellToolTip';
import { HEADER_HEIGHT, PAGE_BOTTOM_PADDING, PAGE_LAYOUT, PAGE_TOP_PADDING } from '~/lib/constants';
import { pageCount as pageCountOptions } from '~/lib/constants/list.constants';
import localStorageConstants from '~/lib/constants/localStorage.constants';
import {
  CareTeamProviderType,
  TABLE_DEFAULT_COLUMNS,
  TABLE_INITIAL_COLUMNS,
  headerTextKeyArray,
  headerMapFromTableHeads,
} from '~/lib/constants/patients.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useLocalStorage from '~/lib/hooks/useLocalStorage';
import {
  getAgeFromDOB,
  getInitialFilterValue,
  getPatientListNote,
  getPatientListNoteActivityDate,
  getPreferredFullName,
  isFilterEmpty,
  validateFormatColumnFilterValue,
  validateFormatColumnSortValue,
  validateFormatPatientFilterValue,
} from '~/lib/util/patient.util';
import util from '~/lib/util/util';
import { Path } from '~/routes/constants';
import { useGetUniversalStatusesEnumQuery } from '~/store/services/enumerations';
import { useGetPatientsListQuery } from '~/store/services/patients';

import PatientColumnFilters from './PatientColumnFilters';
import PatientFilterParams from './PatientFilterParams';
import PatientFilters from './PatientFilters';
import PatientTableActionBar from './PatientTableActionBar';
import PatientTableCareTeamCell from './PatientTableCareTeamCell';
import PatientTableSurveyActivityCell from './PatientTableSurveyActivityCell';

const TABLE_FOOTER_HEIGHT = '113px';

const TOP_LEVEL_ID = 'patients-table';

const generateCellId = (patientId: string, columnId: string): string =>
  `${TOP_LEVEL_ID}__row-${patientId}__column-${columnId}`;

const DATE_FORMAT_IDENTIFIER = 'LL/dd/yyyy';

const { primary } = EquipUIFireflyV2Theme.colors;

const PatientsTable = () => {
  const { currentUser } = useGlobalContext();

  const availableHeaders = useMemo(
    () => headerTextKeyArray.filter((x) => x != 'adminEpisodeStatus'),
    [],
  );

  const { data: universalStatuses } = useGetUniversalStatusesEnumQuery();

  const {
    storedValue: filterValueLocalStorage,
    setValue: setFilterValueLocalStorage,
  } = useLocalStorage<PatientListFiltersType>(
    localStorageConstants.patientFilter,
    getInitialFilterValue(currentUser),
  );

  const {
    storedValue: columnFilterValueLocalStorage,
    setValue: setColumnFilterValueLocalStorage,
  } = useLocalStorage<string[]>(
    localStorageConstants.patientColumnFilter,
    TABLE_INITIAL_COLUMNS,
  );

  const {
    storedValue: columnSortOrderLocalStorage,
    setValue: setColumnSortOrderLocalStorage,
  } = useLocalStorage<string[]>(
    localStorageConstants.patientColumnSortOrder,
    availableHeaders,
  );

  const [selectedFilter, setSelectedFilter] = useState<PatientListFiltersType>(
    validateFormatPatientFilterValue(filterValueLocalStorage, currentUser),
  );

  const [selectedColumnFilter, setSelectedColumnFilter] = useState<string[]>(
    validateFormatColumnFilterValue(
      columnFilterValueLocalStorage,
      TABLE_DEFAULT_COLUMNS,
      TABLE_INITIAL_COLUMNS,
    ),
  );

  const [selectedHeaderSortOrder, setSelectedColumnSortOrder] = useState<
    string[]
  >(
    validateFormatColumnSortValue(
      columnSortOrderLocalStorage,
      availableHeaders,
    ),
  );

  const [pageNumber, setPageNumber] = useState<number>(1);
  const { setComponentVisibility } = useComponentPortalContext();

  const { storedValue: pageSize, setValue: setPageSize } =
    useLocalStorage<MenuSelectOption>(
      localStorageConstants.patientPageCount,
      pageCountOptions[0],
    );

  const filterBy = useMemo(() => {
    const filterParams: PatientListFilterParamType = {};
    const {
      admitDateFrom,
      admitDateTo,
      careTeamMember,
      episodeLevels,
      episodeStatusReasons,
      insuranceVerificationStatus,
      isPatientPaused,
      medicalClearanceStatus,
      name,
      payor,
      payorProgram,
      primaryDiagnosis,
      psychClearanceStatuses,
      treatmentPrograms,
      universalStatuses,
    } = selectedFilter;

    if (name) {
      filterParams['patient-name'] = name;
    }
    if (isPatientPaused) {
      filterParams['is-paused'] = isPatientPaused;
    }

    if (primaryDiagnosis.length > 0) {
      filterParams['primary-diagnosis'] = primaryDiagnosis?.map(
        ({ value }) => value,
      );
    }

    if (insuranceVerificationStatus.length > 0) {
      filterParams['insurance-status'] = insuranceVerificationStatus;
    }

    if (medicalClearanceStatus.length > 0) {
      filterParams['medical-clearance-status'] = medicalClearanceStatus;
    }

    if (careTeamMember.length > 0) {
      filterParams['care-team-member'] = careTeamMember?.map(
        ({ value }) => value,
      );
    }
    if (admitDateFrom) {
      filterParams['admit-start-date'] = admitDateFrom?.value ?? '';
    }
    if (admitDateTo) {
      filterParams['admit-end-date'] = admitDateTo?.value ?? '';
    }
    if (payor.length) filterParams['payor-name'] = payor;
    if (payorProgram.length) filterParams['payor-program-name'] = payorProgram;

    if (episodeLevels?.length > 0)
      filterParams['episode-levels'] = episodeLevels;

    if (treatmentPrograms?.length > 0)
      filterParams['treatment-programs'] = treatmentPrograms;

    if (psychClearanceStatuses?.length > 0)
      filterParams['psych-clearance-details'] = psychClearanceStatuses;

    if (episodeStatusReasons?.length > 0)
      filterParams['episode-closed-reasons'] = episodeStatusReasons;

    if (universalStatuses?.length > 0) {
      filterParams['universal-status'] = universalStatuses?.map(
        ({ value }) => value,
      );
    }

    return filterParams;
  }, [selectedFilter]);

  const { data: patients, isFetching: isLoading } = useGetPatientsListQuery({
    filterBy,
    pageNumber,
    pageSize: Number(pageSize.value),
  });

  const handlePageSelect = (page: number): void => {
    setPageNumber(page);
  };

  useEffect(() => {
    if (!pageSize || !util.isPageCountProper(pageSize)) {
      setPageSize(pageCountOptions[0]);
    }
  }, [pageSize]);

  useEffect(() => {
    setFilterValueLocalStorage(selectedFilter);
  }, [selectedFilter]);

  useEffect(() => {
    setColumnFilterValueLocalStorage(selectedColumnFilter);
  }, [selectedColumnFilter]);

  useEffect(() => {
    setColumnSortOrderLocalStorage(selectedHeaderSortOrder);
  }, [selectedHeaderSortOrder]);

  useEffect(() => {
    handlePageSelect(1);
  }, [selectedFilter, pageSize]);

  useEffect(() => {
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = prev;
    };
  }, []);

  const filterCareTeamMembers = (
    careTeamMembers: PatientListCareTeamMember[],
    providerTypes: CareTeamProviderType[],
  ): string[] =>
    (careTeamMembers ?? [])
      .filter(({ providerTypeName }) =>
        (providerTypes as string[]).includes(providerTypeName.toLowerCase()),
      )
      .map(({ providerFirstName: fn, providerLastName: ln }) => `${fn} ${ln}`);

  const patientRowObjects = useMemo<RowObject<PatientRow>[]>(() => {
    return (patients?.data ?? []).map((patient: PatientListObject) => {
      const {
        admitDate,
        avatarSrc,
        careTeamMembers,
        chosenName,
        dateOfBirth,
        daysOfCare,
        episodeLevel,
        episodeStatus,
        episodeStatusReason,
        externalId,
        firstName,
        insuranceStatus,
        isPaused,
        lastName,
        medicalClearanceStatus,
        notes,
        payorName,
        payorProgramName,
        primaryDiagnosis,
        psychClearanceStatus,
        state,
        surveyActivities,
        treatmentProgramName,
        universalStatusCode,
        weeksAtEquip,
      } = patient;

      const getCareTeamCell = (
        idKey: string,
        providerTypeEnums: CareTeamProviderType[],
      ) => (
        <PatientTableCareTeamCell
          id={generateCellId(externalId, idKey)}
          selectedFilter={selectedFilter?.careTeamMember}
          value={filterCareTeamMembers(careTeamMembers, providerTypeEnums)}
        />
      );
      const getAdminEpisodeStatusCell = (status: string, isPaused: boolean) => (
        <ChakraFlex>
          <ChakraText
            id={generateCellId(externalId, 'episode-status')}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {status || '-'}
          </ChakraText>
          {isPaused && (
            <ChakraBadge
              as="span"
              background="tertiary.marigold.background"
              borderRadius="full"
              color="tertiary.marigold.base"
              marginLeft="10px"
              paddingX="8px"
              textTransform="none"
            >
              Paused
            </ChakraBadge>
          )}
        </ChakraFlex>
      );

      const getSimpleCell = (
        idKey: string,
        value: string,
        isTruncated = false,
        showToolTip = false,
      ) => {
        const body = (
          <ChakraText
            id={generateCellId(externalId, idKey)}
            maxWidth={isTruncated ? '150px' : undefined}
            overflow={isTruncated ? 'hidden' : undefined}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {value || '-'}
          </ChakraText>
        );
        return !showToolTip ? (
          body
        ) : (
          <PatientTableCellToolTip
            content={value || ''}
            id={generateCellId(externalId, idKey)}
          >
            {body}
          </PatientTableCellToolTip>
        );
      };

      const getNote = (type: PatientListNoteType): PatientListNote =>
        notes?.find(
          ({ noteType }: PatientListNote) =>
            noteType?.toLowerCase() === type?.toLowerCase(),
        );

      const lastNote = [...(notes ?? [])]
        .filter((note: PatientListNote) => note.noteActivityDate)
        .sort(
          ({ noteActivityDate: a }, { noteActivityDate: b }) =>
            new Date(b).getTime() - new Date(a).getTime(),
        )[0];

      return {
        key: `RowObject::${firstName}${lastName}::${externalId}`,

        object: {
          adminEpisodeStatus: getAdminEpisodeStatusCell(
            episodeStatus,
            isPaused,
          ),
          admissionsSpecialist: getCareTeamCell('admissions-specialist', [
            CareTeamProviderType.ADMISSIONS_SPECIALIST,
          ]),
          admitDate: getSimpleCell(
            'admit-date',
            admitDate &&
              DateTime.fromSQL(admitDate).toFormat(DATE_FORMAT_IDENTIFIER),
            true,
          ),
          consentFormNote: getSimpleCell(
            'consent-form-date',
            getPatientListNoteActivityDate(getNote('Consent forms')),
          ),
          daysOfCare: getSimpleCell('days-of-care', daysOfCare),
          dietitian: getCareTeamCell('dietitian', [
            CareTeamProviderType.DIETITIAN,
          ]),
          episodeLevel: getSimpleCell('episode-level', episodeLevel),
          episodeStatusReason: getSimpleCell(
            'episode-status-reason',
            episodeStatusReason,
          ),
          familyExperienceNavigator: getCareTeamCell(
            'family-experience-navigator',
            [CareTeamProviderType.FAMILY_EXPERIENCE_NAVIGATOR],
          ),
          caregiverCoach: getCareTeamCell('caregiver-coach', [
            CareTeamProviderType.CAREGIVER_COACH,
            CareTeamProviderType.FAMILY_MENTOR,
          ]),
          fenIntroNote: getSimpleCell(
            'fen-note-date',
            getPatientListNoteActivityDate(getNote('FEN introduction')),
          ),
          // inquiryDate,
          insuranceStatus: getSimpleCell('insurance-status', insuranceStatus),
          intakeSurvey: (
            <PatientTableSurveyActivityCell
              id={generateCellId(externalId, 'intake-survey')}
              surveyActivities={surveyActivities}
              surveyName="Intake"
            />
          ),
          lastNote: getSimpleCell(
            'last-note',
            getPatientListNote(lastNote?.noteContent),
            true,
            true,
          ),
          lastNoteDate: getSimpleCell(
            'last-note-date',
            getPatientListNoteActivityDate(lastNote),
          ),
          medicalClearanceStatus: getSimpleCell(
            'medical-clearance-status',
            medicalClearanceStatus,
          ),
          medicalProvider: getCareTeamCell('medical-provider', [
            CareTeamProviderType.NURSE_PRACTITIONER,
            CareTeamProviderType.PHYSICIAN,
            CareTeamProviderType.PHYSICIAN_ASSISTANT,
            CareTeamProviderType.PSYCHIATRIST,
          ]),
          name: (
            <Link
              style={{ userSelect: 'none' }}
              to={`${Path.MAUD_PATIENTS}/${externalId}`}
            >
              <UserAvatarCard
                avatarSrc={avatarSrc}
                id={generateCellId(externalId, 'name')}
                primaryText={[
                  getPreferredFullName(chosenName, firstName, lastName),
                ]}
                primaryTextBold
                primaryTextFontSize="12px"
                primaryTextLineHeight="17px"
                secondaryText={
                  dateOfBirth
                    ? [
                        `${DateTime.fromSQL(dateOfBirth).toFormat(
                          DATE_FORMAT_IDENTIFIER,
                        )} (${getAgeFromDOB(
                          dateOfBirth,
                          'yyyy-LL-dd',
                        )} years old)`,
                      ]
                    : []
                }
                secondaryTextStyle={{
                  color: 'neutral.secondary',
                  fontSize: '10px',
                  lineHeight: '14px',
                }}
                zIndex={0}
              />
            </Link>
          ),
          // nextTaskDueDate,
          // nextTasks,
          patientServiceRepresentative: getCareTeamCell(
            'patient-service-representative',
            [CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE],
          ),
          payorName: getSimpleCell('payor', payorName, true, true),
          payorProgramName: getSimpleCell(
            'payor-program',
            payorProgramName,
            true,
            true,
          ),
          recoveryCoach: getCareTeamCell('recovery-coach', [
            CareTeamProviderType.RECOVERY_COACH,
            CareTeamProviderType.PEER_MENTOR, 
          ]),
          phoneScreenNote: getSimpleCell(
            'phone-screen-note-date',
            getPatientListNoteActivityDate(getNote('Phone screen')),
          ),
          presentingProblemNote: getSimpleCell(
            'presenting-problem-note-date',
            getPatientListNoteActivityDate(getNote('Presenting problem')),
          ),
          // primaryContactEmail,
          // primaryContactName,
          // primaryContactPhone,
          primaryDiagnosis: getSimpleCell(
            'primary-diagnosis',
            primaryDiagnosis,
            true,
            true,
          ),
          psychClearanceStatus: getSimpleCell(
            'psych-clearance-status',
            psychClearanceStatus,
          ),
          qualifyingCallNote: getSimpleCell(
            'qualifying-call-note-date',
            getPatientListNoteActivityDate(getNote('Qualifying call')),
          ),
          state: getSimpleCell('state', state),
          therapist: getCareTeamCell('therapist', [
            CareTeamProviderType.THERAPIST,
          ]),
          treatmentProgramName: getSimpleCell(
            'treatment-program-name',
            treatmentProgramName,
          ),
          universalStatus: getSimpleCell(
            'universal-status',
            universalStatuses?.find((x) => x.code === universalStatusCode)
              ?.description || universalStatusCode,
          ),
          weeksAtEquip: getSimpleCell('weeks-at-equip', weeksAtEquip),
        },
        value: externalId,
      } as unknown as RowObject<PatientRow>; // TODO remove this casting, bad practice, do not copy
    });
  }, [patients?.data, selectedFilter?.careTeamMember]);

  const tableHeaderRef = useRef<HTMLDivElement>(null);

  const selectedTableHeads = useMemo<TableColumn<PatientRow>[]>(() => {
    return (selectedHeaderSortOrder ?? [])
      .map((columnItem: string) => headerMapFromTableHeads[columnItem])
      .filter((head): head is TableColumn<PatientRow> => head !== undefined)
      .filter((head) => (selectedColumnFilter ?? []).includes(head.key));
  }, [selectedColumnFilter, selectedHeaderSortOrder, headerMapFromTableHeads]);

  return (
    <ChakraBox
      height="100%"
      paddingRight={PAGE_LAYOUT.rightSpacing}
      width="100%"
    >
      <ChakraBox paddingBottom={PAGE_BOTTOM_PADDING} paddingTop={PAGE_TOP_PADDING} ref={tableHeaderRef}>
        <PatientTableActionBar
          headerText="Patient List"
          isDataLoading={isLoading}
          patientCount={patients?.totalCount ?? 0}
        />

        <ChakraFlex alignItems="center" paddingTop="24px">
          {!isFilterEmpty(selectedFilter) && (
            <PatientFilterParams
              filter={selectedFilter}
              setFilter={setSelectedFilter}
            />
          )}
          <ChakraFlex alignItems="center" gap="8px" marginLeft="auto">
            {Object.keys(headerMapFromTableHeads).length > 0 && (
              <PatientColumnFilters
                headerMapFromTableHeads={headerMapFromTableHeads}
                onFilterChange={setSelectedColumnFilter}
                onSortChange={setSelectedColumnSortOrder}
                selectedFilter={selectedColumnFilter}
                selectedSortOrder={selectedHeaderSortOrder}
              />
            )}
            <PatientFilters
              onFilterChange={setSelectedFilter}
              selectedFilter={selectedFilter}
            />
          </ChakraFlex>
        </ChakraFlex>
      </ChakraBox>
      {!isLoading && (
        <>
          {util.isEmptyArray(patients?.data) ? (
            <EmptyList
              actionButtons={[
                {
                  buttonText: 'Reset Filter',
                  key: 'resetFilter',
                  onClick: () => {
                    setSelectedFilter(getInitialFilterValue(undefined));
                  },
                  rightIcon: (
                    <Reset fill={primary[100]} height="16px" width="16px" />
                  ),
                },
                {
                  buttonText: 'Add New Patient',
                  key: 'addNewPatient',
                  onClick: () =>
                    setComponentVisibility({
                      component: PortalComponent.ADD_PATIENT_ACCORDION,
                      data: {},
                      isExpanded: true,
                      isVisible: true,
                    }),
                  rightIcon: (
                    <Add fill={primary[100]} height="16px" width="16px" />
                  ),
                },
              ]}
              description="If you didn’t find what you’re looking for, try to reduce your filters or add a new patient."
              id={TOP_LEVEL_ID}
            />
          ) : (
            <>
              <ChakraBox
                maxHeight={`calc(100vh - ${HEADER_HEIGHT} - ${TABLE_FOOTER_HEIGHT} - ${
                  tableHeaderRef?.current?.clientHeight ?? 0
                }px - 16px)`}
                overflowY="auto"
              >
                <Table<PatientRow>
                  columns={selectedTableHeads}
                  id={TOP_LEVEL_ID}
                  rows={patientRowObjects}
                />
              </ChakraBox>
              <ChakraFlex height={TABLE_FOOTER_HEIGHT}>
                <ChakraFlex alignItems="end" width="100%">
                  <ChakraBox marginBottom="2px" marginTop="10px">
                    <MenuSelect
                      id={TOP_LEVEL_ID}
                      onSelect={setPageSize}
                      options={pageCountOptions}
                      selectedValues={pageSize}
                    />
                  </ChakraBox>
                  <ChakraBox flexGrow={1}>
                    <ChakraFlex
                      justifyContent="center"
                      paddingBottom="8px"
                      paddingTop="16px"
                    >
                      <PageCount
                        id={TOP_LEVEL_ID}
                        pageNumber={pageNumber}
                        pageSize={Number(pageSize.value)}
                        totalCount={patients?.totalCount}
                      />
                    </ChakraFlex>
                    <Pagination
                      current={pageNumber}
                      id={TOP_LEVEL_ID}
                      onPageSelect={handlePageSelect}
                      total={Math.ceil(
                        patients.totalCount / Number(pageSize.value),
                      )}
                    />
                  </ChakraBox>
                </ChakraFlex>
              </ChakraFlex>
            </>
          )}
        </>
      )}
    </ChakraBox>
  );
};

export default PatientsTable;
